<script setup lang="ts">
import AITripDescription from '@apps/main/new-desktop/core/components/AITripDescription.vue';
import { PremadeTripType } from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useItineraryStore,
  usePaxStore,
  usePremadeTripStore,
  usePriceCalendarStore,
} from '@apps/main/new-desktop/core/stores';

import { AppPortableText } from '@libs/ui';

import { useTripOverviewPageStore } from '../../stores';

import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';
import TripOverviewHeaderActions from './TripOverviewHeaderActions.vue';

const tripOverviewPageStore = useTripOverviewPageStore();

const premadeTripStore = usePremadeTripStore();
const browserStore = useBrowserStore();
const paxStore = usePaxStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface TripOverviewHeaderDesktopProps {
  printMode: boolean;
}

const props = defineProps<TripOverviewHeaderDesktopProps>();

interface TripOverviewHeaderDesktopEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'map-click'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
  (eventName: 'open-premade-trip'): void;
  (eventName: 'navigate-to-car-trip'): void;
}

const emit = defineEmits<TripOverviewHeaderDesktopEmits>();

function openPaxSelectorModal(): void {
  emit('pax-click');
}

function openEditTripModal(): void {
  emit('edit-trip');
}

function onHomeAirportClick(): void {
  emit('home-airport-click');
}

function openMonthsSelectorModal(): void {
  emit('months-selector-click');
}

function openDynamicFullScreenMap(): void {
  emit('map-click');
}

function openPremadeTripDetailsModal(): void {
  emit('open-premade-trip');
}

function navigateToCarTrip(): void {
  emit('navigate-to-car-trip');
}
</script>

<template>
  <template v-if="premadeTripStore.showingPremadeTrip">
    <PremadeTripHeaderMobile
      v-if="browserStore.isSmallerThan1200px && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @show-full-description="openPremadeTripDetailsModal"
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
  </template>

  <div v-if="!props.printMode" class="actions">
    <TripOverviewHeaderActions
      :paxes-button-text="paxStore.getPaxesButtonDetailedText"
      :home-flight-data="tripOverviewPageStore.homeFlightData"
      :trip-start-date="itineraryStore.tripStartDate"
      :number-of-destinations="tripOverviewPageStore.numberOfDestinations"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :price-calendar-button-text="priceCalendarStore.calendarTriggerText"
      :first-destination-name="tripOverviewPageStore.getFirstDestinationName()"
      :is-user-selected-itinerary="
        itineraryStore.userSelectedItinerary !== undefined
      "
      @pax-click="openPaxSelectorModal"
      @home-airport-click="onHomeAirportClick"
      @edit-trip="openEditTripModal()"
      @months-selector-click="openMonthsSelectorModal()"
      @map-click="openDynamicFullScreenMap"
      @navigate-to-car-trip="navigateToCarTrip"
    />
  </div>

  <div
    v-if="
      browserStore.isSmallerThan1200px && premadeTripStore.showingPremadeTrip
    "
    class="separator"
  />
</template>

<style scoped lang="scss">
.actions {
  padding: 0 var(--spacing-24);
  background-color: var(--color-white);
  @include for-less-or-equal-than-1199-px {
    padding: 0 var(--spacing-24) var(--spacing-8) var(--spacing-24);
  }
  @include for-less-or-equal-than-599-px {
    padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-24);
  }
}

.separator {
  border-bottom: 1px solid var(--color-neutral-30);
  margin-bottom: var(--spacing-8);
}
</style>
